import { useCallback, useEffect, useState } from "react"
import { useMediaQuery } from "../../../hooks"
import { getApiDataPromise } from "../../../models/ApiData"
import { ExchangeSession } from "../../../models/ExchangeSession"
import { IOption } from "../../../types"
import { UrlUtils } from "../../../utils/UrlUtils"
import { toNotNaNNumberOr } from "../../../utils/utils"
import { BuyBlock } from "./BuyBlock"
import { SellBlock } from "./SellBlock"

const DEFAULT_FIAT_AMOUNT = process.env.REACT_APP_DEFAULT_FIAT_AMOUNT || "200"

export type CardType = "1" | "2"
export type CurrencyDto = { rate: string; code: string; name: string }
type CurrenciesResponseDto = {
  crypto: Record<string, CurrencyDto>
  fiat: Record<string, CurrencyDto>
}
export type CurrencyType = IOption<string, CurrencyDto>

type Props = {
  isBuyType: boolean
}

export const BottomBlock = ({ isBuyType }: Props) => {
  const isMobile = useMediaQuery("(max-width: 768px)")
  const [cardType, setCardType] = useState<CardType>("1")

  const [cryptoAmount, setCryptoAmount] = useState("")
  const [fiatAmount, setFiatAmount] = useState(DEFAULT_FIAT_AMOUNT)
  const [cryptoOptions, setCryptoOptions] = useState<CurrencyType[]>([])
  const [fiatOptions, setFiatOptions] = useState<CurrencyType[]>([])
  const [fiatCurrency, setFiatCurrency] = useState<CurrencyDto | null>(null)
  const [cryptoCurrency, setCryptoCurrency] = useState<CurrencyDto | null>(null)

  const handleChangeFiatAmount = useCallback(
    (amount: string) => {
      setFiatAmount(amount)

      const amountValue = toNotNaNNumberOr(amount, 0)
      const fiatRateValue = toNotNaNNumberOr(fiatCurrency?.rate, 1)
      const cryptoRateValue = toNotNaNNumberOr(cryptoCurrency?.rate, 1)

      setCryptoAmount((amountValue / fiatRateValue / cryptoRateValue).toFixed(6))
    },
    [cryptoCurrency?.rate, fiatCurrency?.rate],
  )

  const handleChangeFiatCurrency = useCallback(
    (currency: CurrencyDto) => {
      setFiatCurrency(currency)

      const amountValue = toNotNaNNumberOr(fiatAmount, 0)
      const fiatRateValue = toNotNaNNumberOr(currency?.rate, 1)
      const cryptoRateValue = toNotNaNNumberOr(cryptoCurrency?.rate, 1)

      setCryptoAmount((amountValue / fiatRateValue / cryptoRateValue).toFixed(6))
    },
    [cryptoCurrency?.rate, fiatAmount],
  )

  const handleChangeCryptoAmount = useCallback(
    (amount: string) => {
      setCryptoAmount(amount)

      const amountValue = toNotNaNNumberOr(amount, 0)
      const fiatRateValue = toNotNaNNumberOr(fiatCurrency?.rate, 1)
      const cryptoRateValue = toNotNaNNumberOr(cryptoCurrency?.rate, 1)

      setCryptoAmount((amountValue * fiatRateValue * cryptoRateValue).toFixed(6))
    },
    [cryptoCurrency?.rate, fiatCurrency?.rate],
  )

  const handleChangeCryptoCurrency = useCallback(
    (currency: CurrencyDto) => {
      setCryptoCurrency(currency)

      const amountValue = toNotNaNNumberOr(cryptoAmount, 0)
      const fiatRateValue = toNotNaNNumberOr(fiatCurrency?.rate, 1)
      const cryptoRateValue = toNotNaNNumberOr(currency?.rate, 1)

      setCryptoAmount((amountValue * fiatRateValue * cryptoRateValue).toFixed(6))
    },
    [cryptoAmount, fiatCurrency?.rate],
  )

  useEffect(() => {
    if (cryptoAmount === "" && fiatCurrency?.rate != null && cryptoCurrency?.rate != null) {
      handleChangeFiatAmount(DEFAULT_FIAT_AMOUNT)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptoCurrency?.rate, fiatCurrency?.rate, handleChangeFiatAmount])

  const handleFetchCurrencies = useCallback(() => {
    getApiDataPromise({}, "exchange", "get_currencies").then((res: { data: CurrenciesResponseDto }) => {
      const cryptoOptionsList = Object.entries(res.data.crypto).map(([value, item]) => ({
        value,
        label: value,
        meta: item,
      }))

      const fiatOptionsList = Object.entries(res.data.fiat).map(([value, item]) => ({
        value,
        label: value,
        meta: item,
      }))

      setCryptoOptions(cryptoOptionsList)
      setFiatOptions(fiatOptionsList)
      // @ts-ignore
      setCryptoCurrency(cryptoOptionsList?.[0]?.meta ?? null)
      // @ts-ignore
      setFiatCurrency(fiatOptionsList?.[0]?.meta ?? null)
    })
  }, [])

  const handleBuyClick = useCallback(() => {
    const data = {
      fiat_input: fiatAmount,
      fiat_select: fiatCurrency?.code,
      crypto_input: cryptoAmount,
      crypto_select: cryptoCurrency?.code,
      step: 1,
    }

    ExchangeSession.data = data

    UrlUtils.redirectTo("exchange?step=" + data["step"])
  }, [cryptoAmount, cryptoCurrency?.code, fiatAmount, fiatCurrency?.code])

  const handleSellClick = useCallback(() => {
    const data = {
      fiat_input: fiatAmount,
      fiat_select: fiatCurrency?.code,
      crypto_input: cryptoAmount,
      crypto_select: cryptoCurrency?.code,
      step: 1,
    }

    ExchangeSession.data = data

    UrlUtils.redirectTo("exchange?step=" + data["step"])
  }, [cryptoAmount, cryptoCurrency?.code, fiatAmount, fiatCurrency?.code])

  useEffect(() => {
    handleFetchCurrencies()
  }, [handleFetchCurrencies])

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      {isBuyType ? (
        <BuyBlock
          cryptoAmount={cryptoAmount}
          cryptoCurrency={cryptoCurrency}
          cryptoOptions={cryptoOptions}
          isMobile={isMobile ?? false}
          onCryptoAmountChange={handleChangeCryptoAmount}
          onCryptoCurrencyChange={handleChangeCryptoCurrency}
          fiatAmount={fiatAmount}
          fiatCurrency={fiatCurrency}
          onUpdate={handleFetchCurrencies}
          fiatOptions={fiatOptions}
          onFiatAmountChange={handleChangeFiatAmount}
          onFiatCurrencyChange={handleChangeFiatCurrency}
          onBuyClick={handleBuyClick}
        />
      ) : (
        <SellBlock
          cardType={cardType}
          onCardTypeChange={setCardType}
          onUpdate={handleFetchCurrencies}
          cryptoAmount={cryptoAmount}
          cryptoCurrency={cryptoCurrency}
          cryptoOptions={cryptoOptions}
          isMobile={isMobile ?? false}
          fiatAmount={fiatAmount}
          onFiatAmountChange={handleChangeFiatAmount}
          onCryptoAmountChange={handleChangeCryptoAmount}
          onCryptoCurrencyChange={handleChangeCryptoCurrency}
          fiatCurrency={fiatCurrency}
          fiatOptions={fiatOptions}
          onFiatCurrencyChange={handleChangeFiatCurrency}
          onSellClick={handleSellClick}
        />
      )}
    </div>
  )
}
